import { useMemo, useState } from "react";

import type {
  ContactMergeCandidate,
  ContactMergeCustomField,
  ContactMergeEditorPayload,
} from "../types";

export interface ContactMergeEditorProps {
  candidates: ContactMergeCandidate[];
  errorMessage?: string;
  initialValue?: ContactMergeEditorPayload;
  submitting?: boolean;
  onSubmit(payload: ContactMergeEditorPayload): void;
}

const independentFields = [
  { key: "phone", legacyName: "Phone", label: "联系电话" },
  { key: "phone2", legacyName: "Phone2", label: "备用电话" },
  { key: "remark", legacyName: "Remark", label: "备注" },
  { key: "weixinCode", legacyName: "WeiXinCode", label: "微信号" },
] as const;

const customEditableFields = new Set<ContactMergeCustomField>(["phone2", "remark"]);

/** 判断字段是否支持人工填写。 */
function isCustomEditableField(field: string): field is ContactMergeCustomField {
  return customEditableFields.has(field as ContactMergeCustomField);
}

/** 编辑一个“多个旧联系人合并到一个保留联系人”的任务。 */
export function ContactMergeEditor({
  candidates,
  errorMessage,
  initialValue,
  submitting = false,
  onSubmit,
}: ContactMergeEditorProps) {
  const [targetContactId, setTargetContactId] = useState<number | undefined>(
    initialValue?.targetContactId,
  );
  const [sourceContactIds, setSourceContactIds] = useState<number[]>(
    initialValue?.sourceContactIds ?? [],
  );
  const [fieldResolution, setFieldResolution] = useState<Record<string, number>>(
    initialValue?.fieldResolution ?? {},
  );
  const [fieldCustomValues, setFieldCustomValues] = useState<
    Partial<Record<ContactMergeCustomField, string | null>>
  >(initialValue?.fieldCustomValues ?? {});
  const [confirmedEmptyFields, setConfirmedEmptyFields] = useState<string[]>(
    initialValue?.confirmEmptyOverwriteFields ?? [],
  );
  const [primarySalesUserId, setPrimarySalesUserId] = useState<number | undefined>(
    initialValue?.primarySalesUserId,
  );

  const target = candidates.find((item) => item.contactId === targetContactId);
  const selectedSources = candidates.filter((item) => sourceContactIds.includes(item.contactId));
  const selectableFieldSources = useMemo(
    () => (target ? [target, ...selectedSources] : []),
    [selectedSources, target],
  );
  const primarySalesOwners = useMemo(() => {
    const owners = selectableFieldSources.flatMap((candidate) =>
      candidate.primarySalesUserId
        ? [{
            userId: candidate.primarySalesUserId,
            userName:
              candidate.primarySalesUserName?.trim() || `用户 ${candidate.primarySalesUserId}`,
          }]
        : [],
    );
    return Array.from(new Map(owners.map((owner) => [owner.userId, owner])).values());
  }, [selectableFieldSources]);
  const resolvedPrimarySalesUserId =
    primarySalesOwners.length === 1
      ? primarySalesOwners[0]?.userId
      : primarySalesOwners.some((owner) => owner.userId === primarySalesUserId)
        ? primarySalesUserId
        : undefined;
  const emptyOverwriteFields = useMemo(() => {
    if (!target) return [];
    const byId = new Map(candidates.map((item) => [item.contactId, item]));
    return independentFields
      .filter(({ key }) => {
        if (isCustomEditableField(key) && Object.hasOwn(fieldCustomValues, key)) {
          return !fieldCustomValues[key] && Boolean(target[key]);
        }
        const sourceId = fieldResolution[key];
        if (!sourceId || sourceId === target.contactId) return false;
        const sourceValue = byId.get(sourceId)?.[key];
        return !sourceValue && Boolean(target[key]);
      })
      .map(({ key }) => key);
  }, [candidates, fieldCustomValues, fieldResolution, target]);

  const chooseTarget = (contactId: number) => {
    setTargetContactId(contactId);
    setSourceContactIds((current) => current.filter((item) => item !== contactId));
    setFieldResolution({});
    setFieldCustomValues({});
    setConfirmedEmptyFields([]);
    setPrimarySalesUserId(undefined);
  };

  const toggleSource = (contactId: number, checked: boolean) => {
    setSourceContactIds((current) =>
      checked
        ? Array.from(new Set([...current, contactId]))
        : current.filter((item) => item !== contactId),
    );
  };

  const setFieldSource = (field: string, value: string) => {
    if (value === "custom" && isCustomEditableField(field)) {
      setFieldResolution((current) => {
        const next = { ...current };
        delete next[field];
        return next;
      });
      setFieldCustomValues((current) => ({
        ...current,
        [field]: Object.hasOwn(current, field) ? current[field] : (target?.[field] ?? ""),
      }));
      return;
    }
    const contactId = Number(value);
    setFieldResolution((current) => ({ ...current, [field]: contactId }));
    if (isCustomEditableField(field)) {
      setFieldCustomValues((current) => {
        const next = { ...current };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <section className="contact-merge-editor" aria-label="联系人合并编辑器">
      <fieldset className="contact-merge-editor__candidate-fieldset">
        <legend>1. 选择一个保留联系人和一个或多个待删除联系人</legend>
        <div className="contact-merge-editor__candidate-table-wrap">
          <table
            aria-label="候选联系人列表"
            className="contact-merge-editor__candidate-table"
          >
            <thead>
              <tr>
                <th>保留</th>
                <th>合并删除</th>
                <th>学校</th>
                <th>姓名</th>
                <th>所属销售</th>
                <th>电话</th>
                <th>职务</th>
                <th>Phone2</th>
                <th>Remark</th>
                <th>CollegeDepatmentMajorID</th>
                <th>DepartMajorName</th>
                <th>WeiXinCode</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((candidate) => {
                const isTarget = targetContactId === candidate.contactId;
                const isSource = sourceContactIds.includes(candidate.contactId);
                return (
                  <tr
                    className={
                      isTarget
                        ? "is-target"
                        : isSource
                          ? "is-source"
                          : undefined
                    }
                    key={candidate.contactId}
                  >
                    <td>
                      <input
                        type="radio"
                        name="merge-target"
                        aria-label={`保留 ${candidate.contactName}`}
                        checked={isTarget}
                        onChange={() => chooseTarget(candidate.contactId)}
                      />
                    </td>
                    <td>
                      {isTarget ? (
                        <span className="contact-merge-editor__target-marker">已保留</span>
                      ) : (
                        <input
                          type="checkbox"
                          aria-label={`合并 ${candidate.contactName}`}
                          checked={isSource}
                          onChange={(event) =>
                            toggleSource(candidate.contactId, event.target.checked)
                          }
                        />
                      )}
                    </td>
                    <td>{displayCandidateValue(candidate.schoolName)}</td>
                    <td><strong>{candidate.contactName}</strong></td>
                    <td>{displayCandidateValue(candidate.primarySalesUserName)}</td>
                    <td>{displayCandidateValue(candidate.phone)}</td>
                    <td>{displayCandidateValue(candidate.positionName)}</td>
                    <td>{displayCandidateValue(candidate.phone2)}</td>
                    <td>{displayCandidateValue(candidate.remark)}</td>
                    <td>{displayCandidateValue(candidate.collegeDepartmentMajorId)}</td>
                    <td>{displayCandidateValue(candidate.departMajorName)}</td>
                    <td>{displayCandidateValue(candidate.weixinCode)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </fieldset>

      <div className="contact-merge-editor__matrix">
        <h3>2. 选择保留联系人最终字段值</h3>
        <p>默认保留目标联系人当前值；可逐项选择任意待删除联系人的值。</p>
        {independentFields.map((field) => (
          <div className="contact-merge-editor__field-row" key={field.key}>
            <div>
              <strong>{field.legacyName}</strong>
              <span>{field.label}</span>
            </div>
            <label>
              <span className="sr-only">{field.legacyName} 来源</span>
              <select
                aria-label={`${field.legacyName} 来源`}
                value={
                  isCustomEditableField(field.key) && Object.hasOwn(fieldCustomValues, field.key)
                    ? "custom"
                    : (fieldResolution[field.key] ?? targetContactId ?? "")
                }
                disabled={!target || selectedSources.length === 0}
                onChange={(event) => setFieldSource(field.key, event.target.value)}
              >
                {selectableFieldSources.map((candidate) => (
                  <option key={candidate.contactId} value={candidate.contactId}>
                    {candidate.contactName}：{String(candidate[field.key] ?? "（空）")}
                  </option>
                ))}
                {isCustomEditableField(field.key) ? (
                  <option value="custom">自定义填写</option>
                ) : null}
              </select>
            </label>
            {isCustomEditableField(field.key) && Object.hasOwn(fieldCustomValues, field.key) ? (
              <label className="contact-merge-editor__custom-value">
                <span>{field.legacyName} 自定义值</span>
                {field.key === "remark" ? (
                  <textarea
                    aria-label={`${field.legacyName} 自定义值`}
                    maxLength={2000}
                    value={fieldCustomValues[field.key] ?? ""}
                    onChange={(event) =>
                      setFieldCustomValues((current) => ({
                        ...current,
                        [field.key]: event.target.value,
                      }))
                    }
                  />
                ) : (
                  <input
                    aria-label={`${field.legacyName} 自定义值`}
                    maxLength={30}
                    value={fieldCustomValues[field.key] ?? ""}
                    onChange={(event) =>
                      setFieldCustomValues((current) => ({
                        ...current,
                        [field.key]: event.target.value,
                      }))
                    }
                  />
                )}
              </label>
            ) : null}
          </div>
        ))}

        <div className="contact-merge-editor__field-row" data-testid="department-major-resolution">
          <div>
            <strong>院系专业</strong>
            <span>CollegeDepatmentMajorID + DepartMajorName</span>
            <span>院系专业必须成组选择同一联系人</span>
          </div>
          <label>
            <span className="sr-only">院系专业来源</span>
            <select
              aria-label="院系专业来源"
              value={
                fieldResolution.collegeDepartmentMajorId ??
                fieldResolution.departMajorName ??
                targetContactId ??
                ""
              }
              disabled={!target || selectedSources.length === 0}
              onChange={(event) => {
                const contactId = Number(event.target.value);
                setFieldResolution((current) => ({
                  ...current,
                  collegeDepartmentMajorId: contactId,
                  departMajorName: contactId,
                }));
              }}
            >
              {selectableFieldSources.map((candidate) => (
                <option key={candidate.contactId} value={candidate.contactId}>
                  {candidate.contactName}：
                  {displayCandidateValue(candidate.collegeDepartmentMajorId)} /{" "}
                  {displayCandidateValue(candidate.departMajorName)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="contact-merge-editor__field-row" data-testid="primary-sales-owner">
          <div>
            <strong>所属销售</strong>
            <span>联系人用户表的有效主销售</span>
            <span>合并后按同校其他联系人数量确定</span>
          </div>
          {primarySalesOwners.length > 1 ? (
            <label>
              <span className="sr-only">所属销售</span>
              <select
                aria-label="所属销售"
                value={resolvedPrimarySalesUserId ?? ""}
                onChange={(event) => setPrimarySalesUserId(Number(event.target.value) || undefined)}
              >
                <option value="">自动判定失败时请选择</option>
                {primarySalesOwners.map((owner) => (
                  <option key={owner.userId} value={owner.userId}>
                    {owner.userName}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <output className="contact-merge-editor__readonly-value" aria-label="所属销售">
              {primarySalesOwners[0]?.userName ?? "（空）"}
            </output>
          )}
        </div>
      </div>

      {emptyOverwriteFields.length > 0 ? (
        <div role="alert" className="contact-merge-editor__warning">
          <strong>所选来源为空，将覆盖目标现有值。</strong>
          {emptyOverwriteFields.map((field) => (
            <label key={field}>
              <input
                type="checkbox"
                checked={confirmedEmptyFields.includes(field)}
                onChange={(event) =>
                  setConfirmedEmptyFields((current) =>
                    event.target.checked
                      ? Array.from(new Set([...current, field]))
                      : current.filter((item) => item !== field),
                  )
                }
              />
              确认以空值覆盖 {field}
            </label>
          ))}
        </div>
      ) : null}

      {errorMessage ? (
        <div role="alert" className="contact-merge-editor__error">
          {errorMessage}
        </div>
      ) : null}

      <button
        type="button"
        disabled={
          submitting ||
          !targetContactId ||
          sourceContactIds.length === 0 ||
          emptyOverwriteFields.some((field) => !confirmedEmptyFields.includes(field))
        }
        onClick={() => {
          if (!targetContactId) return;
          const payload: ContactMergeEditorPayload = {
            targetContactId,
            sourceContactIds,
            fieldResolution,
            ...(resolvedPrimarySalesUserId
              ? { primarySalesUserId: resolvedPrimarySalesUserId }
              : {}),
            ...(Object.keys(fieldCustomValues).length > 0
              ? { fieldCustomValues }
              : {}),
          };
          if (confirmedEmptyFields.length > 0) {
            payload.confirmEmptyOverwriteFields = confirmedEmptyFields;
          }
          onSubmit(payload);
        }}
      >
        保存合并任务
      </button>
    </section>
  );
}

/** 将候选联系人空值转换为稳定、可读的表格文本。 */
function displayCandidateValue(value?: string): string {
  const text = value?.trim();
  return text || "（空）";
}
